// S&OP API Service for communicating with S&OP backend services
import axios, { AxiosResponse } from 'axios';
import {
  PlanningDataPoint,
  PlanningDataRequest,
  PlanningDataResponse,
  BulkUpdateRequest,
  BulkUpdateResponse,
  Scenario,
  ScenarioRequest,
  ScenarioResponse,
  ScenarioComparisonRequest,
  ScenarioComparisonResponse,
  AIInsight,
  InsightsRequest,
  InsightsResponse,
  NLQueryRequest,
  NLQueryResponse,
  WarehouseListResponse,
  ChatSession,
  ChatMessage
} from '../types/sop';
import { handleApiError } from './errorHandler';
import { startLoading, stopLoading } from './loadingManager';
import { getSopApiUrl } from '../config/api';

// Create axios instance for S&OP API
const sopApiClient = axios.create({
  baseURL: getSopApiUrl(),
  timeout: 30000, // S&OP operations can take longer
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for S&OP API
sopApiClient.interceptors.request.use(
  (config) => {
    // Add timestamp for request tracking
    config.metadata = { startTime: Date.now() };
    
    // Add authentication token if available (for multi-tenant support)
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`S&OP API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for S&OP API
sopApiClient.interceptors.response.use(
  (response) => {
    // Log response time in development
    if (process.env.NODE_ENV === 'development' && response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      console.log(`S&OP API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`);
    }
    
    return response;
  },
  (error) => {
    // Enhanced error logging for S&OP API
    const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
    console.error(`S&OP API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} (${duration}ms)`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    return Promise.reject(error);
  }
);

export class SOPService {
  /**
   * Get planning data with optional filters
   */
  static async getPlanningData(request: PlanningDataRequest = {}): Promise<PlanningDataPoint[]> {
    const loadingKey = 'sop_get_planning_data';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_get_planning_data',
        message: 'Loading S&OP planning data...',
        estimatedDuration: 3000
      });

      const params = new URLSearchParams();
      if (request.warehouse_id) params.append('warehouse_id', request.warehouse_id);
      if (request.start_week) params.append('start_week', request.start_week);
      if (request.end_week) params.append('end_week', request.end_week);

      const response: AxiosResponse<PlanningDataResponse> = await sopApiClient.get(
        `/api/v1/planning/data?${params.toString()}`
      );

      return response.data.planning_data || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_get_planning_data',
        component: 'SOPService',
        additionalData: { request }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Update a single planning data point
   */
  static async updatePlanningData(data: PlanningDataPoint): Promise<PlanningDataPoint> {
    const loadingKey = `sop_update_planning_${data.warehouse_id}_${data.week_date}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_update_planning_data',
        message: 'Updating planning data...',
        estimatedDuration: 2000
      });

      const response: AxiosResponse<{ planning_data: PlanningDataPoint }> = await sopApiClient.put(
        '/api/v1/planning/data',
        data
      );

      return response.data.planning_data;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_update_planning_data',
        component: 'SOPService',
        additionalData: { data }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Bulk update multiple planning data points
   */
  static async bulkUpdatePlanningData(updates: PlanningDataPoint[]): Promise<BulkUpdateResponse> {
    const loadingKey = 'sop_bulk_update_planning';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_bulk_update_planning',
        message: `Updating ${updates.length} planning records...`,
        estimatedDuration: 5000
      });

      const request: BulkUpdateRequest = { updates };
      const response: AxiosResponse<BulkUpdateResponse> = await sopApiClient.post(
        '/api/v1/planning/bulk-update',
        request
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_bulk_update_planning',
        component: 'SOPService',
        additionalData: { updateCount: updates.length }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get list of available warehouses
   */
  static async getWarehouses(): Promise<string[]> {
    const loadingKey = 'sop_get_warehouses';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_get_warehouses',
        message: 'Loading warehouses...',
        estimatedDuration: 1500
      });

      const response: AxiosResponse<WarehouseListResponse> = await sopApiClient.get('/api/v1/planning/warehouses');
      return response.data.warehouses || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_get_warehouses',
        component: 'SOPService'
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Create a new scenario
   */
  static async createScenario(request: ScenarioRequest): Promise<Scenario> {
    const loadingKey = 'sop_create_scenario';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_create_scenario',
        message: 'Creating scenario...',
        estimatedDuration: 4000
      });

      const response: AxiosResponse<ScenarioResponse> = await sopApiClient.post(
        '/api/v1/scenarios',
        request
      );

      return response.data.scenario;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_create_scenario',
        component: 'SOPService',
        additionalData: { request }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get all scenarios
   */
  static async getScenarios(): Promise<Scenario[]> {
    const loadingKey = 'sop_get_scenarios';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_get_scenarios',
        message: 'Loading scenarios...',
        estimatedDuration: 2000
      });

      const response: AxiosResponse<{ scenarios: Scenario[] }> = await sopApiClient.get('/api/v1/scenarios');
      return response.data.scenarios || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_get_scenarios',
        component: 'SOPService'
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Compare multiple scenarios
   */
  static async compareScenarios(request: ScenarioComparisonRequest): Promise<ScenarioComparisonResponse> {
    const loadingKey = 'sop_compare_scenarios';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_compare_scenarios',
        message: 'Comparing scenarios...',
        estimatedDuration: 6000
      });

      const response: AxiosResponse<ScenarioComparisonResponse> = await sopApiClient.post(
        '/api/v1/scenarios/compare',
        request
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_compare_scenarios',
        component: 'SOPService',
        additionalData: { request }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get AI-generated insights
   */
  static async getInsights(request: InsightsRequest = {}): Promise<AIInsight[]> {
    const loadingKey = 'sop_get_insights';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_get_insights',
        message: 'Loading AI insights...',
        estimatedDuration: 4000
      });

      const params = new URLSearchParams();
      if (request.warehouse_ids?.length) {
        request.warehouse_ids.forEach(id => params.append('warehouse_id', id));
      }
      if (request.severity_levels?.length) {
        request.severity_levels.forEach(level => params.append('severity', level));
      }
      if (request.insight_types?.length) {
        request.insight_types.forEach(type => params.append('type', type));
      }
      if (request.limit) params.append('limit', request.limit.toString());

      // Use the correct endpoint path that works with the API Gateway configuration
      const response: AxiosResponse<InsightsResponse> = await sopApiClient.get(
        `/api/v1/insights/list?${params.toString()}`
      );

      return response.data.insights || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_get_insights',
        component: 'SOPService',
        additionalData: { request }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Process natural language query
   */
  static async processNaturalLanguageQuery(request: NLQueryRequest): Promise<NLQueryResponse> {
    const loadingKey = 'sop_nl_query';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_nl_query',
        message: 'Processing query...',
        estimatedDuration: 8000 // AI processing can take longer
      });

      const response: AxiosResponse<NLQueryResponse> = await sopApiClient.post(
        '/api/v1/query',
        request
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_nl_query',
        component: 'SOPService',
        additionalData: { query: request.query }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get chat session history
   */
  static async getChatSession(sessionId: string): Promise<ChatSession> {
    const loadingKey = `sop_get_chat_session_${sessionId}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_get_chat_session',
        message: 'Loading chat history...',
        estimatedDuration: 2000
      });

      const response: AxiosResponse<{ session: ChatSession }> = await sopApiClient.get(
        `/api/v1/query/sessions/${sessionId}`
      );

      return response.data.session;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_get_chat_session',
        component: 'SOPService',
        additionalData: { sessionId }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Create new chat session
   */
  static async createChatSession(): Promise<ChatSession> {
    const loadingKey = 'sop_create_chat_session';
    
    try {
      startLoading(loadingKey, {
        operation: 'sop_create_chat_session',
        message: 'Starting new chat session...',
        estimatedDuration: 2000
      });

      const response: AxiosResponse<{ session: ChatSession }> = await sopApiClient.post(
        '/api/v1/query/sessions'
      );

      return response.data.session;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'sop_create_chat_session',
        component: 'SOPService'
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Check S&OP API health
   */
  static async checkHealth(): Promise<{
    healthy: boolean;
    message: string;
    timestamp: string;
  }> {
    try {
      const response = await sopApiClient.get('/api/v1/planning/health', { timeout: 5000 });
      return {
        healthy: true,
        message: 'S&OP API is healthy and responding',
        timestamp: response.data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      return {
        healthy: false,
        message: 'S&OP API is not responding',
        timestamp: new Date().toISOString()
      };
    }
  }
}

export default SOPService;